import React, { createContext, useContext, useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { ServerCity, ServerPlan } from "lib/types";

import { usePlansQuery } from "./use-plans-query";
import { generatePlansQueryKey } from "store/utils";

type PlansProviderProps = {
  initialPlans?: ServerPlan[];
  initialSelectedCity?: ServerCity;
};

type PlansContextType = {
  plans?: ServerPlan[];
  isLoading: boolean;
  isError: boolean;
  selectedCity?: ServerCity;
  handleCityChange: (city: ServerCity) => void;
};

const PlansContext = createContext<PlansContextType>({
  plans: [],
  isLoading: false,
  isError: false,
  selectedCity: undefined,
  handleCityChange: () => null,
});

export const PlansProvider: React.FC<
  React.PropsWithChildren<PlansProviderProps>
> = ({ children, initialPlans, initialSelectedCity }) => {
  const [plans, setPlans] = useState<ServerPlan[] | undefined>(
    initialPlans || undefined,
  );
  const [selectedCity, setSelectedCity] = useState<ServerCity | undefined>(
    initialSelectedCity || undefined,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const queryClient = useQueryClient();

  const plansQuery = usePlansQuery(
    selectedCity?.name ?? "",
    initialPlans || [],
  );

  useEffect(() => {
    setPlans(plansQuery.data ?? []);
    setIsLoading(plansQuery.isLoading);
    setIsError(plansQuery.isError);
  }, [plansQuery.data, plansQuery.isLoading, plansQuery.isError]);

  const handleCityChange = (city: ServerCity) => {
    setSelectedCity(city);

    const refetchResponse = queryClient.refetchQueries(
      {
        queryKey: generatePlansQueryKey(city.name ?? ""),
      },
      { throwOnError: true },
    );

    refetchResponse.then(() => setIsLoading(false));
  };

  return (
    <PlansContext.Provider
      value={{
        plans,
        isLoading,
        isError,
        selectedCity,
        handleCityChange,
      }}
    >
      {children}
    </PlansContext.Provider>
  );
};

export const usePlansContext = () => {
  const context = useContext(PlansContext);

  if (context === undefined) {
    throw new Error("usePlansContext must be used within a PlansProvider");
  }

  return context as PlansContextType;
};
